import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Logo from '@/components/Logo';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import ImageUpload from '@/components/ImageUpload';
import { User, Mail, Lock, Eye, EyeOff, ArrowRight, MapPin, Stethoscope, Heart, Building2, Baby, Bone, Brain, Glasses, Smile, Syringe, Pill, Scissors, HeartPulse, Activity, Calendar, Loader2, Users, Camera } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'patient',
    date_of_birth: '',
    birth_day: '',
    birth_month: '',
    birth_year: '',
    gender: '',
    location: '',
    latitude: null,
    longitude: null,
    facility_type: '',
    other_facility_type: '', // For "Autre (à préciser)"
    professional_type: '',
    specialty: '',
    license_number: '',
    // New facility fields
    facility_legal_name: '',
    facility_year_created: '',
    facility_address: '',
    facility_address_lat: null,
    facility_address_lon: null,
    facility_phone: '',
    facility_email: '',
    // Manager fields
    manager_first_name: '',
    manager_last_name: '',
    manager_phone: '',
    // Profile picture (optional)
    profile_image: '',
  });

  // Generate arrays for date dropdowns
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: '01', label: 'Janvier' },
    { value: '02', label: 'Février' },
    { value: '03', label: 'Mars' },
    { value: '04', label: 'Avril' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Juin' },
    { value: '07', label: 'Juillet' },
    { value: '08', label: 'Août' },
    { value: '09', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' },
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 120 }, (_, i) => currentYear - i);

  // Update date_of_birth when day/month/year change
  useEffect(() => {
    if (formData.birth_day && formData.birth_month && formData.birth_year) {
      const day = formData.birth_day.toString().padStart(2, '0');
      const dateStr = `${formData.birth_year}-${formData.birth_month}-${day}`;
      setFormData(prev => ({ ...prev, date_of_birth: dateStr }));
    }
  }, [formData.birth_day, formData.birth_month, formData.birth_year]);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const roles = [
    { value: 'patient', label: 'Patient', icon: Heart, description: 'Je cherche des soins de santé' },
    { value: 'professional', label: 'Professionnel', icon: Stethoscope, description: 'Médecin, infirmier, sage-femme...' },
    { value: 'facility', label: 'Établissement', icon: Building2, description: 'Clinique, hôpital, laboratoire' },
    { value: 'agent', label: 'Agent Terrain', icon: Users, description: 'Recruteur de professionnels/établissements' },
  ];

  const professionalTypes = [
    { value: 'doctor', label: 'Médecin', icon: Stethoscope, requiresSpecialty: true },
    { value: 'nurse', label: 'Infirmier(ère)', icon: Syringe, requiresSpecialty: false },
    { value: 'midwife', label: 'Sage-femme', icon: Baby, requiresSpecialty: false },
    { value: 'physiotherapist', label: 'Kinésithérapeute', icon: Bone, requiresSpecialty: false },
    { value: 'dentist', label: 'Dentiste', icon: Smile, requiresSpecialty: true },
    { value: 'pharmacist', label: 'Pharmacien(ne)', icon: Pill, requiresSpecialty: false },
    { value: 'psychologist', label: 'Psychologue', icon: Brain, requiresSpecialty: false },
    { value: 'optician', label: 'Opticien(ne)', icon: Glasses, requiresSpecialty: false },
    { value: 'lab_technician', label: 'Technicien de laboratoire', icon: Activity, requiresSpecialty: false },
    { value: 'radiologist', label: 'Radiologue / Imagerie', icon: HeartPulse, requiresSpecialty: false },
    { value: 'surgeon', label: 'Chirurgien', icon: Scissors, requiresSpecialty: true },
    { value: 'other', label: 'Autre professionnel', icon: User, requiresSpecialty: false },
  ];

  const medicalSpecialties = [
    { value: 'general', label: 'Médecine générale' },
    { value: 'cardiology', label: 'Cardiologie' },
    { value: 'pediatrics', label: 'Pédiatrie' },
    { value: 'gynecology', label: 'Gynécologie-Obstétrique' },
    { value: 'dermatology', label: 'Dermatologie' },
    { value: 'ophthalmology', label: 'Ophtalmologie' },
    { value: 'ent', label: 'ORL (Oto-rhino-laryngologie)' },
    { value: 'neurology', label: 'Neurologie' },
    { value: 'psychiatry', label: 'Psychiatrie' },
    { value: 'orthopedics', label: 'Orthopédie' },
    { value: 'gastroenterology', label: 'Gastro-entérologie' },
    { value: 'pulmonology', label: 'Pneumologie' },
    { value: 'nephrology', label: 'Néphrologie' },
    { value: 'endocrinology', label: 'Endocrinologie' },
    { value: 'rheumatology', label: 'Rhumatologie' },
    { value: 'urology', label: 'Urologie' },
    { value: 'oncology', label: 'Oncologie' },
    { value: 'anesthesiology', label: 'Anesthésie-Réanimation' },
    { value: 'radiology', label: 'Radiologie' },
    { value: 'emergency', label: 'Médecine d\'urgence' },
    { value: 'internal_medicine', label: 'Médecine interne' },
    { value: 'other', label: 'Autre spécialité' },
  ];

  const dentalSpecialties = [
    { value: 'general_dentistry', label: 'Dentisterie générale' },
    { value: 'orthodontics', label: 'Orthodontie' },
    { value: 'periodontics', label: 'Parodontologie' },
    { value: 'endodontics', label: 'Endodontie' },
    { value: 'oral_surgery', label: 'Chirurgie buccale' },
    { value: 'pediatric_dentistry', label: 'Dentisterie pédiatrique' },
    { value: 'prosthodontics', label: 'Prothèse dentaire' },
  ];

  const surgeonSpecialties = [
    { value: 'general_surgery', label: 'Chirurgie générale' },
    { value: 'cardiac_surgery', label: 'Chirurgie cardiaque' },
    { value: 'neurosurgery', label: 'Neurochirurgie' },
    { value: 'orthopedic_surgery', label: 'Chirurgie orthopédique' },
    { value: 'plastic_surgery', label: 'Chirurgie plastique' },
    { value: 'pediatric_surgery', label: 'Chirurgie pédiatrique' },
    { value: 'thoracic_surgery', label: 'Chirurgie thoracique' },
    { value: 'vascular_surgery', label: 'Chirurgie vasculaire' },
  ];

  const facilityTypes = [
    { value: 'hospital', label: 'Hôpital' },
    { value: 'clinic', label: 'Clinique' },
    { value: 'health_center', label: 'Centre de santé' },
    { value: 'pharmacy', label: 'Pharmacie' },
    { value: 'laboratory', label: 'Laboratoire' },
    { value: 'imaging_center', label: "Centre d'imagerie" },
    { value: 'maternity', label: 'Maternité' },
    { value: 'dental_clinic', label: 'Cabinet dentaire' },
    { value: 'other', label: 'Autre (à préciser)' },
  ];

  const getSpecialtiesForType = (type) => {
    switch(type) {
      case 'doctor':
        return medicalSpecialties;
      case 'dentist':
        return dentalSpecialties;
      case 'surgeon':
        return surgeonSpecialties;
      default:
        return [];
    }
  };

  const requiresSpecialty = () => {
    const profType = professionalTypes.find(p => p.value === formData.professional_type);
    return profType?.requiresSpecialty || false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      // For facilities, use manager name as full_name
      if (formData.role === 'facility') {
        if (!formData.facility_type) {
          toast.error("Veuillez sélectionner le type d'établissement");
          return;
        }
        if (formData.facility_type === 'other' && !formData.other_facility_type) {
          toast.error("Veuillez préciser le type d'établissement");
          return;
        }
        if (!formData.facility_legal_name) {
          toast.error("Veuillez entrer la désignation légale de l'établissement");
          return;
        }
        if (!formData.facility_phone) {
          toast.error("Veuillez entrer le téléphone de l'établissement");
          return;
        }
        if (!formData.facility_address) {
          toast.error("Veuillez entrer l'adresse de l'établissement");
          return;
        }
        if (!formData.manager_first_name || !formData.manager_last_name) {
          toast.error("Veuillez entrer le nom complet du responsable");
          return;
        }
        if (!formData.manager_phone) {
          toast.error("Veuillez entrer le téléphone du responsable");
          return;
        }
        if (!formData.email || !formData.password) {
          toast.error('Veuillez remplir l\'email et le mot de passe');
          return;
        }
      } else {
        // For non-facility roles
        if (!formData.full_name || !formData.email || !formData.password) {
          toast.error('Veuillez remplir tous les champs obligatoires');
          return;
        }
      }
      if (formData.password.length < 6) {
        toast.error('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Veuillez entrer une adresse e-mail valide');
        return;
      }
      // For professionals, require professional type
      if (formData.role === 'professional' && !formData.professional_type) {
        toast.error('Veuillez sélectionner votre type de profession');
        return;
      }
      // For some professional types, require specialty
      if (formData.role === 'professional' && requiresSpecialty() && !formData.specialty) {
        toast.error('Veuillez sélectionner votre spécialité');
        return;
      }
      setStep(2);
      return;
    }

    setLoading(true);

    // Create a timeout promise to prevent infinite loading
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('TIMEOUT'));
      }, 60000); // 60 seconds timeout
    });

    try {
      // For facilities, construct full_name from manager name
      const fullName = formData.role === 'facility' 
        ? `${formData.manager_first_name} ${formData.manager_last_name}`.trim()
        : formData.full_name;
      
      const userData = {
        ...formData,
        full_name: fullName,
        date_of_birth: formData.date_of_birth || null,
        age: formData.date_of_birth ? calculateAge(formData.date_of_birth) : null,
        profile_image: formData.profile_image || null,
        facility_type: formData.role === 'facility' 
          ? (formData.facility_type === 'other' ? formData.other_facility_type : formData.facility_type) 
          : null,
        professional_type: formData.role === 'professional' ? formData.professional_type : null,
        specialty: formData.role === 'professional' ? formData.specialty : null,
        license_number: formData.role === 'professional' ? formData.license_number : null,
        // Facility additional data
        facility_legal_name: formData.role === 'facility' ? formData.facility_legal_name : null,
        facility_year_created: formData.role === 'facility' ? formData.facility_year_created : null,
        facility_address: formData.role === 'facility' ? formData.facility_address : null,
        facility_address_lat: formData.role === 'facility' ? formData.facility_address_lat : null,
        facility_address_lon: formData.role === 'facility' ? formData.facility_address_lon : null,
        facility_phone: formData.role === 'facility' ? formData.facility_phone : null,
        facility_email: formData.role === 'facility' ? formData.facility_email : null,
        manager_first_name: formData.role === 'facility' ? formData.manager_first_name : null,
        manager_last_name: formData.role === 'facility' ? formData.manager_last_name : null,
        manager_phone: formData.role === 'facility' ? formData.manager_phone : null,
      };
      
      // Race between registration and timeout
      const { user, otpSent } = await Promise.race([
        register(userData),
        timeoutPromise
      ]);
      
      if (otpSent) {
        toast.success('Inscription réussie ! Vérifiez votre e-mail.');
        navigate('/verify-otp', { 
          state: { 
            email: user.email,
            userRole: user.role 
          } 
        });
      } else {
        toast.success('Inscription réussie !');
        navigate('/dashboard');
      }
    } catch (error) {
      // Handle timeout error specifically
      if (error.message === 'TIMEOUT') {
        toast.error(
          "L'inscription prend plus de temps que prévu. Veuillez vérifier votre e-mail ou réessayer dans quelques instants.",
          { duration: 8000 }
        );
        setLoading(false);
        return;
      }
      
      // Handle network errors
      if (!error.response) {
        toast.error(
          "Problème de connexion. Vérifiez votre connexion internet et réessayez.",
          { duration: 6000 }
        );
        setLoading(false);
        return;
      }
      
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "Erreur lors de l'inscription";
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <header className="p-4 md:p-6">
        <Link to="/" data-testid="register-logo">
          <Logo size="sm" />
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 pb-8">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl rounded-2xl md:rounded-3xl overflow-hidden">
            {/* Header gradient bar */}
            <div className="h-2 bg-gradient-to-r from-[#E53935] via-[#2196F3] to-[#1A237E]" />
            
            <CardHeader className="text-center pt-6 md:pt-8 pb-2 px-4 md:px-6">
              <CardTitle 
                className="text-xl md:text-2xl lg:text-3xl font-bold text-[#1A237E]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Créer un compte
              </CardTitle>
              <CardDescription className="text-sm md:text-base text-[#64748B]">
                {step === 1 ? 'Informations de connexion' : 'Informations personnelles'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 md:px-6 lg:px-8 pt-4 md:pt-6 pb-6 md:pb-8">
              {/* Progress indicator */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className={`w-3 h-3 rounded-full transition-colors ${step >= 1 ? 'bg-[#E53935]' : 'bg-slate-200'}`} />
                <div className={`w-16 h-1 rounded transition-colors ${step >= 2 ? 'bg-[#E53935]' : 'bg-slate-200'}`} />
                <div className={`w-3 h-3 rounded-full transition-colors ${step >= 2 ? 'bg-[#E53935]' : 'bg-slate-200'}`} />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                {step === 1 ? (
                  <>
                    {/* Role Selection */}
                    <div className="space-y-2 md:space-y-3">
                      <Label className="text-sm font-medium text-[#1A237E]">Vous êtes</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {roles.map((role) => {
                          const Icon = role.icon;
                          const isSelected = formData.role === role.value;
                          return (
                            <button
                              key={role.value}
                              type="button"
                              onClick={() => setFormData({ 
                                ...formData, 
                                role: role.value, 
                                facility_type: '', 
                                professional_type: '',
                                specialty: ''
                              })}
                              className={`p-2 md:p-3 rounded-xl border-2 transition-all ${
                                isSelected 
                                  ? 'border-[#E53935] bg-[#FFEBEE]' 
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                              data-testid={`role-${role.value}`}
                            >
                              <Icon className={`w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 ${isSelected ? 'text-[#E53935]' : 'text-slate-400'}`} />
                              <span className={`text-[10px] md:text-xs font-medium block ${isSelected ? 'text-[#E53935]' : 'text-slate-600'}`}>
                                {role.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Professional Type Selection - only shown when role is professional */}
                    {formData.role === 'professional' && (
                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-[#1A237E]">Type de professionnel *</Label>
                        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                          {professionalTypes.map((prof) => {
                            const Icon = prof.icon;
                            const isSelected = formData.professional_type === prof.value;
                            return (
                              <button
                                key={prof.value}
                                type="button"
                                onClick={() => setFormData({ 
                                  ...formData, 
                                  professional_type: prof.value,
                                  specialty: ''
                                })}
                                className={`p-2 rounded-lg border-2 transition-all text-center ${
                                  isSelected 
                                    ? 'border-[#E53935] bg-[#FFEBEE]' 
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                                data-testid={`prof-type-${prof.value}`}
                              >
                                <Icon className={`w-4 h-4 mx-auto mb-1 ${isSelected ? 'text-[#E53935]' : 'text-slate-400'}`} />
                                <span className={`text-[9px] font-medium block leading-tight ${isSelected ? 'text-[#E53935]' : 'text-slate-600'}`}>
                                  {prof.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {/* Specialty Selection - for doctors, dentists, surgeons */}
                        {requiresSpecialty() && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#1A237E]">Spécialité *</Label>
                            <Select
                              value={formData.specialty}
                              onValueChange={(value) => setFormData({ ...formData, specialty: value })}
                            >
                              <SelectTrigger className="h-11 rounded-xl border-slate-200 text-sm" data-testid="specialty-select">
                                <SelectValue placeholder="Sélectionnez votre spécialité" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {getSpecialtiesForType(formData.professional_type).map((spec) => (
                                  <SelectItem key={spec.value} value={spec.value}>
                                    {spec.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* License Number */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-[#1A237E]">
                            N° Ordre / Licence (optionnel)
                          </Label>
                          <Input
                            value={formData.license_number}
                            onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                            placeholder="Ex: ONMC-12345"
                            className="h-11 rounded-xl border-slate-200 text-sm"
                            data-testid="license-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Facility Type Selection - only shown when role is facility */}
                    {formData.role === 'facility' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-[#1A237E]">Type d&apos;établissement *</Label>
                          <Select
                            value={formData.facility_type}
                            onValueChange={(value) => setFormData({ ...formData, facility_type: value, other_facility_type: '' })}
                          >
                            <SelectTrigger className="h-11 md:h-12 rounded-xl border-slate-200 text-sm md:text-base" data-testid="facility-type-select">
                              <SelectValue placeholder="Sélectionnez le type" />
                            </SelectTrigger>
                            <SelectContent>
                              {facilityTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Other facility type input */}
                        {formData.facility_type === 'other' && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#1A237E]">Précisez le type *</Label>
                            <Input
                              value={formData.other_facility_type}
                              onChange={(e) => setFormData({ ...formData, other_facility_type: e.target.value })}
                              placeholder="Ex: Centre de dialyse, Maison de retraite..."
                              className="h-11 rounded-xl border-slate-200 text-sm"
                              data-testid="other-facility-type-input"
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-[#1A237E]">Désignation légale *</Label>
                          <Input
                            value={formData.facility_legal_name}
                            onChange={(e) => setFormData({ ...formData, facility_legal_name: e.target.value })}
                            placeholder="Nom officiel de l'établissement"
                            className="h-11 rounded-xl border-slate-200 text-sm"
                            data-testid="facility-legal-name-input"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#1A237E]">Année de création</Label>
                            <Input
                              type="number"
                              value={formData.facility_year_created}
                              onChange={(e) => setFormData({ ...formData, facility_year_created: e.target.value })}
                              placeholder="Ex: 2015"
                              min="1900"
                              max={new Date().getFullYear()}
                              className="h-11 rounded-xl border-slate-200 text-sm"
                              data-testid="facility-year-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#1A237E]">Téléphone *</Label>
                            <Input
                              value={formData.facility_phone}
                              onChange={(e) => setFormData({ ...formData, facility_phone: e.target.value })}
                              placeholder="6XXXXXXXX"
                              className="h-11 rounded-xl border-slate-200 text-sm"
                              data-testid="facility-phone-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-[#1A237E]">Adresse de l&apos;établissement *</Label>
                          <AddressAutocomplete
                            value={formData.facility_address}
                            onChange={(value) => setFormData({ ...formData, facility_address: value })}
                            onSelect={(location) => {
                              setFormData(prev => ({
                                ...prev,
                                facility_address: location.display_name,
                                facility_address_lat: location.lat,
                                facility_address_lon: location.lon
                              }));
                            }}
                            placeholder="Rechercher l'adresse..."
                            inputClassName="h-11 rounded-xl border-slate-200 text-sm"
                            data-testid="facility-address-input"
                          />
                        </div>

                        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                          <p className="text-sm font-medium text-blue-900 mb-3">Responsable de l&apos;établissement</p>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-blue-700">Prénom *</Label>
                              <Input
                                value={formData.manager_first_name}
                                onChange={(e) => setFormData({ ...formData, manager_first_name: e.target.value })}
                                placeholder="Prénom"
                                className="h-10 rounded-lg text-sm"
                                data-testid="manager-firstname-input"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-blue-700">Nom *</Label>
                              <Input
                                value={formData.manager_last_name}
                                onChange={(e) => setFormData({ ...formData, manager_last_name: e.target.value })}
                                placeholder="Nom"
                                className="h-10 rounded-lg text-sm"
                                data-testid="manager-lastname-input"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-blue-700">Téléphone du responsable *</Label>
                            <Input
                              value={formData.manager_phone}
                              onChange={(e) => setFormData({ ...formData, manager_phone: e.target.value })}
                              placeholder="6XXXXXXXX"
                              className="h-10 rounded-lg text-sm"
                              data-testid="manager-phone-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Full name field - Hidden for facilities since we use manager name */}
                    {formData.role !== 'facility' && (
                      <div className="space-y-2">
                        <Label htmlFor="full_name" className="text-sm font-medium text-[#1A237E]">
                          Nom complet *
                        </Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[#64748B]" />
                          <Input
                            id="full_name"
                            type="text"
                            placeholder="Jean Dupont"
                            value={formData.full_name}
                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                            className="pl-10 md:pl-11 h-11 md:h-12 rounded-xl border-slate-200 text-sm md:text-base"
                            required
                            data-testid="register-name-input"
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-[#1A237E]">
                        Adresse e-mail *
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[#64748B]" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="email@exemple.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="pl-10 md:pl-11 h-11 md:h-12 rounded-xl border-slate-200 text-sm md:text-base"
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-sm font-medium text-[#1A237E]">
                        Mot de passe *
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[#64748B]" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Minimum 6 caractères"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="pl-10 md:pl-11 pr-10 md:pr-11 h-11 md:h-12 rounded-xl border-slate-200 text-sm md:text-base"
                          required
                          data-testid="register-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#1A237E] p-1"
                          aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4 md:w-5 md:h-5" /> : <Eye className="w-4 h-4 md:w-5 md:h-5" />}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Photo de profil - Optionnel */}
                    <div className="space-y-3 mb-4">
                      <Label className="text-sm font-medium text-[#1A237E] flex items-center gap-2">
                        <Camera className="w-4 h-4" />
                        Photo de profil <span className="text-slate-400 font-normal">(facultatif)</span>
                      </Label>
                      <div className="flex items-center gap-4">
                        <ImageUpload
                          currentImage={formData.profile_image}
                          onImageUploaded={(url) => setFormData({ ...formData, profile_image: url })}
                          type="profile"
                          size="md"
                        />
                        <div className="flex-1">
                          <p className="text-xs text-slate-500">
                            Ajoutez une photo pour personnaliser votre profil. Vous pourrez la modifier plus tard.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Date de naissance - 3 listes déroulantes */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-[#1A237E]">
                        Date de naissance
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        {/* Jour */}
                        <Select
                          value={formData.birth_day?.toString() || ''}
                          onValueChange={(value) => setFormData({ ...formData, birth_day: value })}
                        >
                          <SelectTrigger className="h-12 md:h-12 rounded-xl border-slate-200 text-sm min-h-[48px]" data-testid="register-birth-day">
                            <SelectValue placeholder="Jour" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px]">
                            {days.map((day) => (
                              <SelectItem key={day} value={day.toString()} className="h-11">
                                {day}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Mois */}
                        <Select
                          value={formData.birth_month || ''}
                          onValueChange={(value) => setFormData({ ...formData, birth_month: value })}
                        >
                          <SelectTrigger className="h-12 md:h-12 rounded-xl border-slate-200 text-sm min-h-[48px]" data-testid="register-birth-month">
                            <SelectValue placeholder="Mois" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px]">
                            {months.map((month) => (
                              <SelectItem key={month.value} value={month.value} className="h-11">
                                {month.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Année */}
                        <Select
                          value={formData.birth_year?.toString() || ''}
                          onValueChange={(value) => setFormData({ ...formData, birth_year: value })}
                        >
                          <SelectTrigger className="h-12 md:h-12 rounded-xl border-slate-200 text-sm min-h-[48px]" data-testid="register-birth-year">
                            <SelectValue placeholder="Année" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px]">
                            {years.map((year) => (
                              <SelectItem key={year} value={year.toString()} className="h-11">
                                {year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.date_of_birth && (
                        <p className="text-xs text-slate-500">
                          Âge: {calculateAge(formData.date_of_birth)} ans
                        </p>
                      )}
                    </div>

                    {/* Genre - Masculin/Féminin uniquement */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-[#1A237E]">
                        Sexe
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, gender: 'male' })}
                          className={`h-11 md:h-12 rounded-xl border-2 font-medium text-sm transition-all ${
                            formData.gender === 'male'
                              ? 'border-[#1976D2] bg-[#1976D2]/10 text-[#1976D2]'
                              : 'border-slate-200 hover:border-slate-300 text-slate-600'
                          }`}
                          data-testid="register-gender-male"
                        >
                          Masculin
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, gender: 'female' })}
                          className={`h-11 md:h-12 rounded-xl border-2 font-medium text-sm transition-all ${
                            formData.gender === 'female'
                              ? 'border-[#E91E63] bg-[#E91E63]/10 text-[#E91E63]'
                              : 'border-slate-200 hover:border-slate-300 text-slate-600'
                          }`}
                          data-testid="register-gender-female"
                        >
                          Féminin
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-sm font-medium text-[#1A237E]">
                        Localisation
                      </Label>
                      <AddressAutocomplete
                        value={formData.location}
                        onChange={(value) => setFormData({ ...formData, location: value })}
                        onSelect={(location) => {
                          const cityName = location.address?.city || location.address?.town || 
                                          location.address?.village || location.display_name.split(',')[0];
                          setFormData(prev => ({
                            ...prev,
                            location: cityName,
                            latitude: location.lat,
                            longitude: location.lon
                          }));
                        }}
                        placeholder="Rechercher votre ville en Afrique..."
                        filterType="cities"
                        showMap={formData.latitude !== null}
                        mapHeight="150px"
                        inputClassName="h-11 md:h-12 rounded-xl border-slate-200 text-sm md:text-base"
                        data-testid="register-location-input"
                      />
                      {formData.latitude && (
                        <p className="text-xs text-emerald-600 flex items-center gap-1">
                          <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                          Localisation confirmée
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="flex gap-2 md:gap-3 pt-2">
                  {step === 2 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1 h-11 md:h-12 rounded-xl border-slate-200 text-sm md:text-base"
                    >
                      Retour
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-11 md:h-12 bg-[#E53935] hover:bg-[#D32F2F] text-white font-semibold rounded-xl transition-all hover:-translate-y-0.5 text-sm md:text-base"
                    data-testid="register-submit-button"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 md:w-5 md:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span className="hidden xs:inline">Inscription...</span>
                      </span>
                    ) : step === 1 ? (
                      <span className="flex items-center gap-2">
                        Continuer
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 md:gap-2">
                        <span className="hidden xs:inline">Créer mon compte</span>
                        <span className="xs:hidden">Créer</span>
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                      </span>
                    )}
                  </Button>
                </div>
              </form>

              <div className="mt-6 md:mt-8 text-center">
                <p className="text-sm md:text-base text-[#64748B]">
                  Déjà un compte?{' '}
                  <Link to="/login" className="text-[#2196F3] hover:text-[#1E88E5] font-semibold">
                    Se connecter
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Register;
